import type { FileSystemTree, WebContainerProcess } from "@webcontainer/api";

import { useCallback, useEffect, useRef, useState } from "react";

import shouldShowV2Debuggers from "@/features/react-editor/utils/shouldShowV2Debuggers";
import { WebContainer } from "@webcontainer/api";

// biome-ignore lint/nursery/noEnum: I'm using the enum so that I can use greater than comparisons
export enum WebContainerStatus {
  Error = -1,
  Booting,
  WaitingForFiles,
  Mounting,
  Installing,
  Starting,
  Downloading,
  Ready,
  Compiling,
  Serving,
}

// Constants
const EXPECTED_PORT = 3001;

// NOTE (Gabe 2025-07-16): This is supposed to prevent interactive terminal
// control characters from being spit out of the node processes we run. It
// doesn't seem to work for next dev, so we also stripAnsiCodes using the
// function below.
const DUMB_TERMINAL_ENVS = {
  NO_COLOR: "1", // Standard opt-out for colored output
  FORCE_COLOR: "0", // Ensures tools like chalk suppress color
  TERM: "dumb", // Prevents terminal formatting
} as const;

// NOTE (Gabe 2025-08-07): The next dev server watcher in the WebContainer gets
// tripped up when files are changed too quickly which sometimes happens at the
// end of chat changes. One way this manifests is by the tailwind generated CSS
// being out of sync with the JS. These env vars are a workaround that forces
// the watcher to use polling to ensure updates are triggered by the final file
// changes.
const WATCH_POLL_ENVS = {
  WATCHPACK_POLLING: "true",
  // NOTE (Gabe 2025-08-07): I'm using 100ms here to avoid excessive CPU usage,
  // but this could be reduced if latency is a problem.
  WATCHPACK_POLLING_INTERVAL: "100",
  // Next uses chokidar for a few non-webpack watches (config/env files,
  // sometimes Tailwind/PostCSS pipelines/tools, etc.).
  CHOKIDAR_USEPOLLING: "true",
  CHOKIDAR_INTERVAL: "100",
} as const;

function stripAnsiCodes(message: string): string {
  return (
    message
      // biome-ignore lint/suspicious/noControlCharactersInRegex: we are specifically looking for control characters
      .replace(/\x1b\[[\d;]*m/g, "") // Remove color codes
      .trim()
  );
}

export type LogMessage = {
  date: Date;
  message: string;
};

type UseWebContainerParams = {
  fileTree: FileSystemTree | undefined;
  projectId: string | undefined;
};
type PageStatusRecord = {
  timestamp: number;
  status: number;
};
type PageStatusMap = Record<string, PageStatusRecord>;
type UseWebContainerReturn = {
  logs: LogMessage[];
  webContainerUrl: string | undefined;
  /**
   * A map of page routes to their latest status resulting from a GET request.
   * This is useful for seeing what pages have been rendered and what their
   * status was when they were rendered. Statuses are 200, 404, 500, etc.
   */
  pageStatusMap: PageStatusMap;
  status: WebContainerStatus;
  setStatus: (status: WebContainerStatus) => void;

  webContainerApi: Pick<WebContainer, "export" | "fs" | "workdir"> | null;

  mountAdditionalFiles: (fileTree: FileSystemTree) => Promise<void>;

  error: any | null;
  restart: () => void;
};

export function useWebContainerHook({
  fileTree,
  projectId,
}: UseWebContainerParams): UseWebContainerReturn {
  const [webContainer, setWebContainer] = useState<WebContainer | undefined>(
    undefined,
  );
  const [status, setStatus] = useState<WebContainerStatus>(
    WebContainerStatus.Booting,
  );
  const [forcedRestartAt, setForcedRestartAt] = useState<number>(0);
  const isNextServerReady = status >= WebContainerStatus.Ready;
  const [error, setError] = useState<any | null>(null);
  const [webContainerUrl, setWebContainerUrl] = useState<string | undefined>(
    undefined,
  );

  const [pageStatusMap, setPageStatusMap] = useState<PageStatusMap>({});

  const devProcessRef = useRef<WebContainerProcess | null>(null);

  const [logs, setLogs] = useState<LogMessage[]>([]);

  const addLogMessage = useCallback((message: string) => {
    const cleanedMessage = stripAnsiCodes(message);

    if (!cleanedMessage) {
      return;
    }

    if (cleanedMessage.includes("Starting development server")) {
      setStatus(WebContainerStatus.Installing);
    }

    if (cleanedMessage.includes("✓ Ready in")) {
      setStatus(WebContainerStatus.Ready);
    }

    if (cleanedMessage.includes("Compiling")) {
      setStatus(WebContainerStatus.Compiling);
    }

    if (cleanedMessage.includes("Downloading swc package")) {
      setStatus(WebContainerStatus.Downloading);
    }

    if (cleanedMessage.startsWith("GET")) {
      setStatus(WebContainerStatus.Serving);
      const [, pageRoute, status] = cleanedMessage.split(" ");
      const statusNumber = Number(status);
      if (!pageRoute || isNaN(statusNumber)) {
        return;
      }
      setPageStatusMap((prev) => ({
        ...prev,
        [pageRoute]: {
          timestamp: Date.now(),
          status: statusNumber,
        },
      }));
    }

    setLogs((prev) => [...prev, { date: new Date(), message: cleanedMessage }]);
  }, []);

  // Effect 1: Boot WebContainer and set up listeners ONCE, and clean up on
  // unmount
  useEffect(() => {
    if (forcedRestartAt > 0) {
      addLogMessage("Forced restart detected");
    }
    let webContainerInstance: WebContainer | undefined;
    async function bootWebContainer() {
      try {
        addLogMessage(`Booting WebContainer for project ${projectId}...`);
        webContainerInstance = await WebContainer.boot();

        webContainerInstance.on("error", (error) => {
          addLogMessage(`WebContainer error: ${error.message}`);
        });

        webContainerInstance.on("server-ready", (port, url) => {
          addLogMessage(`Server ready at ${url}`);

          if (port !== EXPECTED_PORT) {
            return;
          }

          setWebContainerUrl(url);
        });

        setWebContainer(webContainerInstance);

        addLogMessage("WebContainer booted successfully.");
        setStatus(WebContainerStatus.WaitingForFiles);
      } catch (error) {
        addLogMessage(
          `Error booting WebContainer: ${error instanceof Error ? error.message : String(error)}`,
        );
        setError(error);
        setWebContainer(undefined);
      }
    }

    void bootWebContainer();

    return () => {
      if (webContainerInstance) {
        setWebContainerUrl(undefined);
        setStatus(WebContainerStatus.Booting);
        setLogs([]);
        setPageStatusMap({});
        setError(null);
        if (devProcessRef.current) {
          devProcessRef.current.kill();
          devProcessRef.current = null;
        }
        try {
          webContainerInstance.teardown();
        } catch (error) {
          addLogMessage(
            `Error during WebContainer teardown: ${error instanceof Error ? error.message : String(error)}`,
          );
          setError(error);
        }
        setWebContainer(undefined);
      }
    };
  }, [addLogMessage, projectId, forcedRestartAt]);

  const mountFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!fileTree || !webContainer) {
        return;
      }

      try {
        setStatus(WebContainerStatus.Mounting);
        addLogMessage("Mounting files...");
        await webContainer.mount(fileTree);
        addLogMessage("Files mounted successfully.");
      } catch (error) {
        console.error("Failed to mount files:", error);
        addLogMessage(
          `Error mounting files: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
    [addLogMessage, webContainer],
  );
  const setupProject = useCallback(async () => {
    if (!fileTree || !webContainer) {
      return;
    }
    if (devProcessRef.current) {
      addLogMessage("Killing dev process...");
      setWebContainerUrl(undefined);
      devProcessRef.current.kill();
      devProcessRef.current = null;
    }

    try {
      await mountFiles(fileTree);

      setStatus(WebContainerStatus.Installing);
      addLogMessage("Running npm install...");
      const installProcess = await webContainer.spawn("npm", ["install"], {
        env: DUMB_TERMINAL_ENVS,
      });
      void installProcess.output.pipeTo(
        new WritableStream({
          write(data) {
            addLogMessage(data);
          },
        }),
      );
      const installExitCode = await installProcess.exit;
      if (installExitCode !== 0) {
        addLogMessage(`npm install failed with exit code: ${installExitCode}`);
        return;
      }
      addLogMessage("npm install completed.");

      addLogMessage("Starting development server...");
      devProcessRef.current = await webContainer.spawn("npm", ["run", "dev"], {
        env: {
          ...DUMB_TERMINAL_ENVS,
          ...WATCH_POLL_ENVS,
        },
      });
      void devProcessRef.current.output.pipeTo(
        new WritableStream({
          write(data) {
            addLogMessage(data);
          },
        }),
      );
    } catch (error) {
      console.error("Failed to setup project:", error);
      addLogMessage(
        `Error setting up project: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }, [fileTree, addLogMessage, mountFiles, webContainer]);

  // Effect 2: Handle initial setup (npm install + dev server) and file mounting
  useEffect(() => {
    if (!fileTree) {
      return;
    }

    if (!devProcessRef.current) {
      // Only run npm install and start dev server if this is the initial setup
      void setupProject();
    } else {
      addLogMessage("Files updated - dev server will hot reload");
      void mountFiles(fileTree);
    }
  }, [setupProject, fileTree, mountFiles, addLogMessage]);

  const mountAdditionalFiles = useCallback(
    async (fileTree: FileSystemTree) => {
      if (!webContainer) {
        addLogMessage(
          "Cannot mount additional files - WebContainer not booted",
        );
        return;
      }

      addLogMessage("Mounting additional files...");

      return webContainer.mount(fileTree);
    },
    [addLogMessage, webContainer],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "NEXTJS_DEV_INDICATOR_OVERRIDE_REQUEST") {
        event.source?.postMessage(
          {
            type: "NEXTJS_DEV_INDICATOR_OVERRIDE_RESPONSE",
            value: shouldShowV2Debuggers(),
          },
          { targetOrigin: event.origin || "*" },
        );
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return {
    logs,
    status,
    setStatus,
    webContainerUrl: isNextServerReady ? webContainerUrl : undefined,
    pageStatusMap,
    webContainerApi: webContainer ?? null,
    mountAdditionalFiles,
    error,
    restart: () => {
      setForcedRestartAt(Date.now());
    },
  };
}

export function detectBuildError(message: string): boolean {
  const errorPatterns = [
    // Next.js compilation errors
    /failed to compile/i,
    /compilation error/i,
    /module not found/i,
    /cannot resolve module/i,
    /syntaxerror/i,
    /typeerror.*at compile time/i,

    // Module parsing errors
    /module parse failed/i,
    /identifier .* has already been declared/i,
    /duplicate identifier/i,
    /redeclaration of/i,

    // TypeScript errors
    /type error/i,
    /TS\d{4}:/,

    // React/JSX errors
    /jsx element .* has no corresponding closing tag/i,
    /expected corresponding jsx closing tag/i,

    // Import/Export errors
    /module '".*"' has no exported member/i,
    /cannot find module/i,
    /attempted import error/i,

    // Build process failures
    /npm err!/i,
    /build failed/i,
    /error occurred prerendering page/i,

    // Next.js specific errors
    /error: element type is invalid/i,
    /unhandled runtime error/i,
  ];

  return errorPatterns.some((pattern) => pattern.test(message));
}

export function detectFileStatusError(message: string): boolean {
  // HTTP error status codes that indicate file/server errors
  const errorStatusCodes = ["404", "500", "502", "503", "504"];

  const httpRequestPattern = /(get)\s+\/\S+\s+(\d{3})\s+in\s+\d+ms/i;
  const match = message.match(httpRequestPattern);

  if (match) {
    const statusCode = match[2];
    return errorStatusCodes.includes(statusCode ?? "");
  }

  return false;
}

export function detect200StatusCode(message: string): boolean {
  const httpRequestPattern = /(get)\s+\/\S+\s+(\d{3})\s+in\s+\d+ms/i;
  const match = message.match(httpRequestPattern);

  if (match) {
    const statusCode = match[2];
    return statusCode === "200";
  }

  return false;
}
